import { InferType } from 'yup';
import { clinicCaptionSchema } from '@views/pages/Patient/components/DetailsCard/components/ClinicCaption/schemas';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { useEditPatientClinicalCaptionMutation } from '@common/cache';

type IFormShape = InferType<typeof clinicCaptionSchema>;

const initialValues = {
  clinical_caption: '',
};

export const useClinicCaption = ({
  patient_id,
  clinical_caption,
}: {
  patient_id: string;
  clinical_caption: string;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const { mutateAsync: editCaptionAsync, isPending: isSubmitting } =
    useEditPatientClinicalCaptionMutation(patient_id);

  const formMethods = useForm({
    mode: 'onChange',
    resolver: yupResolver(clinicCaptionSchema),
    defaultValues: {
      ...initialValues,
      clinical_caption,
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
  } = formMethods;

  const handleOpenCaptionField = () => {
    setIsOpen(true);
    inputRef.current?.focus();
  };

  const handleCloseCaptionField = () => {
    setIsOpen(false);
    reset({ clinical_caption });
  };

  const submitHandler = async (values: IFormShape) => {
    try {
      await editCaptionAsync(
        values.clinical_caption ? values.clinical_caption.trim() : '',
      );

      reset(values);
      setIsOpen(false);
      toast.success('Changes saved!');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  return {
    formMethods,
    isOpen,
    onSubmit,
    handleOpenCaptionField,
    handleCloseCaptionField,
    isSubmitting,
    isValid,
    isDirty,
    inputRef,
  };
};
